import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Listing } from '../api/client';
import { useSavedListings } from '../context/SavedListingsContext';
import { formatPrice as formatPriceUtil } from '../utils/format';
import { addToCompare, getCompareIds } from '../pages/Compare';
import { useState, useEffect } from 'react';

const PLACEHOLDER_IMAGE = 'data:image/svg+xml,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"><rect fill="%23e5e7eb" width="400" height="300"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-family="sans-serif" font-size="18">No photo</text></svg>'
);

function getFirstImageUrl(listing: Listing): string | null {
  if (listing.thumbnailUrl) return listing.thumbnailUrl;
  if (listing.mediaUrls && listing.mediaUrls.length > 0) return listing.mediaUrls[0];
  const keys = listing.mediaKeys || [];
  const url = keys.find((k) => typeof k === 'string' && k.startsWith('http'));
  return url || null;
}

function formatPrice(listing: Listing): string {
  return formatPriceUtil(listing.price, listing.currency);
}

function formatLocation(listing: Listing): string {
  const loc = listing.location;
  if (!loc) return '—';
  const parts = [loc.city, loc.area].filter(Boolean);
  return parts.length ? `${parts.join(', ')}` : (loc.country || '—');
}

export default function ListingCard({ listing }: { listing: Listing }) {
  const { t, i18n } = useTranslation();
  const [compareIds, setCompareIds] = useState<string[]>(getCompareIds);
  const displayTitle = (i18n.language === 'ar' && listing.titleAr) ? listing.titleAr : (listing.titleEn || '');
  const imageUrl = getFirstImageUrl(listing);
  const location = formatLocation(listing);
  const badge = listing.type === 'sale' ? t('common.forSale') : listing.type === 'rent' ? t('common.forRent') : t('common.shortTerm');
  const { isSaved, toggle } = useSavedListings();
  const saved = isSaved(listing.listingId);
  const inCompare = compareIds.includes(listing.listingId);
  const canAddCompare = compareIds.length < 3 && !inCompare;

  useEffect(() => {
    const handler = () => setCompareIds(getCompareIds());
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const onSaveClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggle(listing);
  };

  const onCompareClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (canAddCompare) addToCompare(listing.listingId);
  };

  return (
    <Link
      to={`/listings/${listing.listingId}`}
      style={{
        display: 'block',
        background: '#fff',
        borderRadius: 10,
        overflow: 'hidden',
        border: '1px solid #e5e7eb',
        textDecoration: 'none',
        color: 'inherit',
        transition: 'box-shadow 0.2s, border-color 0.2s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)';
        e.currentTarget.style.borderColor = '#0d9488';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.borderColor = '#e5e7eb';
      }}
    >
      <div style={{ position: 'relative', aspectRatio: '4/3', background: '#f3f4f6' }}>
        <img
          src={imageUrl || PLACEHOLDER_IMAGE}
          alt={displayTitle}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <button
          type="button"
          onClick={onSaveClick}
          aria-label={saved ? t('listingDetail.removeFromSaved') : t('listingDetail.saveListing')}
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.95)',
            border: '1px solid #e5e7eb',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: saved ? '#dc2626' : '#6b7280',
            padding: 0,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill={saved ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
        </button>
        <span
          style={{
            position: 'absolute',
            bottom: 10,
            left: 10,
            background: '#0d9488',
            color: '#fff',
            fontSize: '0.75rem',
            fontWeight: 600,
            padding: '4px 10px',
            borderRadius: 6,
          }}
        >
          {badge}
        </span>
        {canAddCompare && (
          <button
            type="button"
            onClick={onCompareClick}
            aria-label={t('compare.addToCompare')}
            style={{
              position: 'absolute',
              bottom: 10,
              right: 10,
              padding: '4px 10px',
              borderRadius: 6,
              border: '1px solid #fff',
              background: 'rgba(0,0,0,0.5)',
              color: '#fff',
              fontSize: '0.75rem',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            {t('compare.addToCompare')}
          </button>
        )}
      </div>
      <div style={{ padding: '1rem 1rem 1.25rem' }}>
        <div style={{ fontWeight: 600, fontSize: '1.05rem', marginBottom: 4, color: '#111' }}>
          {displayTitle}
        </div>
        <div style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: 6 }}>
          {location}
        </div>
        <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#0d9488' }}>
          {formatPrice(listing)}
        </div>
        <div style={{ fontSize: '0.8rem', color: '#9ca3af', marginTop: 6, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem 0.75rem' }}>
          {listing.bedrooms != null && (
            <span>{listing.bedrooms} {t('listingDetail.bed')} · {listing.bathrooms ?? '—'} {t('listingDetail.bath')}{listing.areaSqm != null ? ` · ${listing.areaSqm} m²` : ''}</span>
          )}
          {(listing.viewCount ?? 0) > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden>
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              {t('common.views', { count: listing.viewCount ?? 0 })}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
